import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import { PageInterface, PageService } from '@app/services/page.service';
import { ErrorService } from '@app/services/error.service';

import {
	SignatureRemovalRequest,
	SignatureRemovalRequestSearchParams,
	SignatureRemovalRequestSearchParamsInterface,
	SignatureRemovalRequestService,
	SignatureRemovalRequestStatus,
} from '@app/models/signature-removal-request';

type StatusFilter = SignatureRemovalRequestStatus | 'all';

@Component({
	selector: 'app-molecule-coach-signature-removal-request-list',
	templateUrl:
		'./molecule-coach-signature-removal-request-list.component.html',
	styleUrls: [
		'./molecule-coach-signature-removal-request-list.component.less',
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoleculeCoachSignatureRemovalRequestListComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	protected skeleton: PageSkeletonType = 'list';

	requests: SignatureRemovalRequest[] = [];
	searchParams: SignatureRemovalRequestSearchParams;
	loading = false;

	limit = 20;
	currentPageIndex = 1;
	totalItems = 0;

	statusFilter: StatusFilter = 'all';
	statusOptions: StatusFilter[] = ['all', 'pending', 'approved', 'rejected'];

	private unsubscribe$ = new Subject<void>();
	private paginationSubject = new BehaviorSubject<number>(0);

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		private signatureRemovalRequestService: SignatureRemovalRequestService,
		private errorService: ErrorService,
		private cdr: ChangeDetectorRef
	) {
		super(pageService, translate);
	}

	ngOnInit(): void {
		super.ngOnInit();

		const initialParams: Partial<SignatureRemovalRequestSearchParamsInterface> =
			{
				_page: 0,
				_limit: this.limit,
				_sort: 'created_at',
				_order: 'desc',
				_populate: ['stagiaire', 'thematique', 'formation'],
			};
		this.searchParams = new SignatureRemovalRequestSearchParams(
			initialParams
		);

		this.paginationSubject
			.asObservable()
			.pipe(
				takeUntil(this.unsubscribe$),
				tap((newIndex) => {
					const realIndex = newIndex > 1 ? newIndex - 1 : 0;
					this.searchParams.props._page = realIndex;
					this.searchParams.next();
				})
			)
			.subscribe();

		this.searchParams
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(async () => {
				await this.refresh();
			});
	}

	ngAfterViewInit(): void {
		super.ngAfterViewInit();
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant(
				'signature-removal-request_list-title'
			),
			breadcrumbs: [
				{
					label: this.translate.instant(
						'signature-removal-request_list-title'
					),
				},
			],
			hideTopBar: false,
			hideMenu: false,
		};
	}

	trackById = (_: number, request: SignatureRemovalRequest): string => {
		return request.getId();
	};

	async refresh(): Promise<void> {
		this.loading = true;
		this.cdr.markForCheck();
		try {
			const result = await this.signatureRemovalRequestService.list(
				this.searchParams.toObject()
			);
			this.requests = result.items;
			this.totalItems = result.total;
		} catch (err) {
			this.errorService.handle(err);
			this.requests = [];
			this.totalItems = 0;
		}
		this.loading = false;
		this.cdr.markForCheck();
	}

	handleStatusChange(value: StatusFilter): void {
		this.statusFilter = value;
		this.currentPageIndex = 1;
		this.searchParams.props._page = 0;
		if (value === 'all') {
			delete this.searchParams.props.status;
		} else {
			this.searchParams.props.status = value;
		}
		this.searchParams.next();
	}

	handleNextPage(index: number): void {
		this.currentPageIndex = index;
		this.paginationSubject.next(index);
	}

	getStatusColor(status: SignatureRemovalRequestStatus): string {
		switch (status) {
			case 'pending':
				return 'blue';
			case 'approved':
				return 'green';
			case 'rejected':
				return 'red';
			default:
				return 'default';
		}
	}

	getStatusKey(status: StatusFilter): string {
		return 'signature-removal-request_status_' + status;
	}

	getPeriodKey(period: 'morning' | 'afternoon'): string {
		return 'signature-removal-request_period_' + period;
	}

	getThematiqueLabel(request: SignatureRemovalRequest): string {
		const value: any = request.props.thematique;
		if (!value) return '';
		if (typeof value === 'string') return value;
		return value.name || value._id || '';
	}

	getStagiaireLabel(request: SignatureRemovalRequest): string {
		const value: any = request.props.stagiaire;
		if (!value) return '';
		if (typeof value === 'string') return value;
		const fn = value.first_name || '';
		const ln = value.last_name || '';
		const full = (fn + ' ' + ln).trim();
		return full || value._id || '';
	}
}
